import { v4 as uuidv4 } from "uuid";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { addToBackendCart } from "../../services/api/orders.js";
import { setCartSessionToken } from "../../services/api/client.js";
import { convertToBackendCartItem } from "../../utils/cartUtils.js";

/**
 * Async thunk to add item to cart and sync to backend if it's the first item
 * This ensures session tokens are created immediately when shopping starts
 */
export const addToCartWithBackendSync = createAsyncThunk(
  "cart/addToCartWithBackendSync",
  async (product, { getState }) => {
    const state = getState();
    const isFirstItem = state.cart.cartItems.length === 0;

    // Generate cart item ID upfront
    const cartItemId = uuidv4();
    const cartItem = {
      ...product,
      quantity: product.quantity ? product.quantity : 1,
      cartItemId,
      ...(product.customization && { isCustomized: true }),
    };

    // If this is the first item, sync to backend immediately to get session token
    if (isFirstItem) {
      try {
        const backendItem = convertToBackendCartItem(cartItem);
        const response = await addToBackendCart(backendItem);
        const result = await response.json();

        // Extract and store session token from response
        const sessionToken = result.data?.sessionToken || result.sessionToken;
        if (sessionToken) {
          setCartSessionToken(sessionToken);
        }
      } catch (error) {
        console.error("Failed to sync first item to backend:", error);
        // Continue anyway - local cart will still work
      }
    }

    return cartItem;
  },
);

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    cartItems: [],
    paymentSuccess: false,
    isAddingToCart: false,
    isCartOpen: false,
    appliedDiscount: null,
  },
  reducers: {
    addToCart(state, action) {
      const product = action.payload;

      // Handle frame customization
      if (product.customization) {
        // Check if an identical customized item already exists
        const existingCustomizedItem = state.cartItems.find((item) => {
          if (!item.isCustomized || item.id !== product.id) return false;

          // Compare customization objects deeply
          const existingCustomization = item.customization;
          const newCustomization = product.customization;

          // Compare lens type
          if (existingCustomization.lensType?.id !== newCustomization.lensType?.id) return false;

          // Compare color
          if (existingCustomization.color?.name !== newCustomization.color?.name) return false;

          // Compare prescription data
          if (existingCustomization.prescription?.type !== newCustomization.prescription?.type) return false;

          if (newCustomization.prescription?.type === "manual") {
            const existingData = existingCustomization.prescription.data;
            const newData = newCustomization.prescription.data;

            if (!existingData || !newData) return false;

            // Compare prescription values
            if (existingData.pd !== newData.pd) return false;
            if (JSON.stringify(existingData.rightEye) !== JSON.stringify(newData.rightEye)) return false;
            if (JSON.stringify(existingData.leftEye) !== JSON.stringify(newData.leftEye)) return false;
          } else if (newCustomization.prescription?.type === "upload") {
            // For uploaded files, compare file IDs
            const existingFileId = existingCustomization.prescription?.data?.id;
            const newFileId = newCustomization.prescription?.data?.id;
            if (existingFileId !== newFileId) return false;
          } else if (newCustomization.prescription?.data !== existingCustomization.prescription?.data) {
            return false;
          }

          return true;
        });

        if (existingCustomizedItem) {
          // Update quantity of existing item
          state.cartItems = state.cartItems.map((item) => {
            if (item.cartItemId === existingCustomizedItem.cartItemId) {
              return {
                ...item,
                quantity: product.quantity ? item.quantity + product.quantity : item.quantity + 1,
              };
            }
            return item;
          });
        } else {
          // Add new customized item
          state.cartItems.push({
            ...product,
            quantity: product.quantity ? product.quantity : 1,
            cartItemId: uuidv4(),
            isCustomized: true,
          });
        }
        return;
      }

      if (!product.variation) {
        const cartItem = state.cartItems.find((item) => item.id === product.id && !item.isCustomized);
        if (!cartItem) {
          state.cartItems.push({
            ...product,
            quantity: product.quantity ? product.quantity : 1,
            cartItemId: uuidv4(),
          });
        } else {
          state.cartItems = state.cartItems.map((item) => {
            if (item.cartItemId === cartItem.cartItemId) {
              return {
                ...item,
                quantity: product.quantity ? item.quantity + product.quantity : item.quantity + 1,
              };
            }
            return item;
          });
        }
      } else {
        const cartItem = state.cartItems.find(
          (item) =>
            item.id === product.id &&
            product.selectedProductColor &&
            product.selectedProductColor === item.selectedProductColor &&
            product.selectedProductSize &&
            product.selectedProductSize === item.selectedProductSize &&
            (product.cartItemId ? product.cartItemId === item.cartItemId : true),
        );
        if (!cartItem) {
          state.cartItems.push({
            ...product,
            quantity: product.quantity ? product.quantity : 1,
            cartItemId: uuidv4(),
          });
        } else if (
          cartItem !== undefined &&
          (cartItem.selectedProductColor !== product.selectedProductColor ||
            cartItem.selectedProductSize !== product.selectedProductSize)
        ) {
          state.cartItems = [
            ...state.cartItems,
            {
              ...product,
              quantity: product.quantity ? product.quantity : 1,
              cartItemId: uuidv4(),
            },
          ];
        } else {
          state.cartItems = state.cartItems.map((item) => {
            if (item.cartItemId === cartItem.cartItemId) {
              return {
                ...item,
                quantity: product.quantity ? item.quantity + product.quantity : item.quantity + 1,
                selectedProductColor: product.selectedProductColor,
                selectedProductSize: product.selectedProductSize,
              };
            }
            return item;
          });
        }
      }
    },
    deleteFromCart(state, action) {
      state.cartItems = state.cartItems.filter((item) => item.cartItemId !== action.payload);
    },
    decreaseQuantity(state, action) {
      const product = action.payload;
      if (product.quantity === 1) {
        state.cartItems = state.cartItems.filter((item) => item.cartItemId !== product.cartItemId);
      } else {
        state.cartItems = state.cartItems.map((item) =>
          item.cartItemId === product.cartItemId ? { ...item, quantity: item.quantity - 1 } : item,
        );
      }
    },
    deleteAllFromCart(state) {
      state.cartItems = [];
    },
    setPaymentSuccess(state, action) {
      state.paymentSuccess = action.payload;
    },
    clearPaymentSuccess(state) {
      state.paymentSuccess = false;
    },
    updateItemPrices(state, action) {
      const updatedItems = action.payload;
      state.cartItems = updatedItems;
    },
    openCart(state) {
      state.isCartOpen = true;
    },
    closeCart(state) {
      state.isCartOpen = false;
    },
    applyDiscount(state, action) {
      state.appliedDiscount = action.payload;
    },
    removeDiscount(state) {
      state.appliedDiscount = null;
    },
    deleteAllFromCartAndDiscount(state) {
      state.cartItems = [];
      state.appliedDiscount = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addToCartWithBackendSync.pending, (state) => {
        state.isAddingToCart = true;
      })
      .addCase(addToCartWithBackendSync.fulfilled, (state, action) => {
        state.isAddingToCart = false;
        const product = action.meta.arg; // Original product passed to thunk
        const cartItem = action.payload; // Cart item returned from thunk

        // Use the existing addToCart logic by checking product type
        if (product.customization) {
          // Check if an identical customized item already exists
          const existingCustomizedItem = state.cartItems.find((item) => {
            if (!item.isCustomized || item.id !== product.id) return false;

            const existingCustomization = item.customization;
            const newCustomization = product.customization;

            if (existingCustomization.lensType?.id !== newCustomization.lensType?.id) return false;
            if (existingCustomization.color?.name !== newCustomization.color?.name) return false;
            if (existingCustomization.prescription?.type !== newCustomization.prescription?.type) return false;

            if (newCustomization.prescription?.type === "manual") {
              const existingData = existingCustomization.prescription.data;
              const newData = newCustomization.prescription.data;

              if (!existingData || !newData) return false;
              if (existingData.pd !== newData.pd) return false;
              if (JSON.stringify(existingData.rightEye) !== JSON.stringify(newData.rightEye)) return false;
              if (JSON.stringify(existingData.leftEye) !== JSON.stringify(newData.leftEye)) return false;
            } else if (newCustomization.prescription?.type === "upload") {
              const existingFileId = existingCustomization.prescription?.data?.id;
              const newFileId = newCustomization.prescription?.data?.id;
              if (existingFileId !== newFileId) return false;
            } else if (newCustomization.prescription?.data !== existingCustomization.prescription?.data) {
              return false;
            }

            return true;
          });

          if (existingCustomizedItem) {
            state.cartItems = state.cartItems.map((item) => {
              if (item.cartItemId === existingCustomizedItem.cartItemId) {
                return {
                  ...item,
                  quantity: product.quantity ? item.quantity + product.quantity : item.quantity + 1,
                };
              }
              return item;
            });
          } else {
            state.cartItems.push(cartItem);
          }
        } else if (!product.variation) {
          const existingItem = state.cartItems.find((item) => item.id === product.id && !item.isCustomized);
          if (!existingItem) {
            state.cartItems.push(cartItem);
          } else {
            state.cartItems = state.cartItems.map((item) => {
              if (item.cartItemId === existingItem.cartItemId) {
                return {
                  ...item,
                  quantity: product.quantity ? item.quantity + product.quantity : item.quantity + 1,
                };
              }
              return item;
            });
          }
        } else {
          const existingItem = state.cartItems.find(
            (item) =>
              item.id === product.id &&
              product.selectedProductColor &&
              product.selectedProductColor === item.selectedProductColor &&
              product.selectedProductSize &&
              product.selectedProductSize === item.selectedProductSize &&
              (product.cartItemId ? product.cartItemId === item.cartItemId : true),
          );

          if (!existingItem) {
            state.cartItems.push(cartItem);
          } else if (
            existingItem !== undefined &&
            (existingItem.selectedProductColor !== product.selectedProductColor ||
              existingItem.selectedProductSize !== product.selectedProductSize)
          ) {
            state.cartItems.push(cartItem);
          } else {
            state.cartItems = state.cartItems.map((item) => {
              if (item.cartItemId === existingItem.cartItemId) {
                return {
                  ...item,
                  quantity: product.quantity ? item.quantity + product.quantity : item.quantity + 1,
                  selectedProductColor: product.selectedProductColor,
                  selectedProductSize: product.selectedProductSize,
                };
              }
              return item;
            });
          }
        }
      })
      .addCase(addToCartWithBackendSync.rejected, (state, action) => {
        state.isAddingToCart = false;
        console.error("Failed to add item to cart:", action.error);
      });
  },
});

export const {
  addToCart,
  deleteFromCart,
  decreaseQuantity,
  deleteAllFromCart,
  setPaymentSuccess,
  clearPaymentSuccess,
  updateItemPrices,
  openCart,
  closeCart,
  applyDiscount,
  removeDiscount,
  deleteAllFromCartAndDiscount,
} = cartSlice.actions;
export default cartSlice.reducer;
